/**
 * A5 portrait (vertical) HTML for client receipts — office paper printers.
 */

import { buildPrintAdsCssVariables, buildPrintAdsInkForceCss } from '../utils/printAdsPalette';
import { getReceiptLabel, normalizeReceiptLanguage } from '../utils/receiptLabels';
import { formatSafeDateTime } from '../utils/formatSafety';
import {
  getActiveCurrencyLabel,
  readActiveCurrencyType,
  toDisplayAmount
} from '../utils/posCurrencyUtils';
import {
  getSaleLineActiveAmount,
  getSaleLineActiveQuantity,
  getSaleLineUnitPrice
} from '../domain/sales/saleLineMath';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatMoney(amount, language) {
  const mro = Number(amount) || 0;
  const currency = readActiveCurrencyType();
  const label = getActiveCurrencyLabel(language, { form: 'short' });
  const display = toDisplayAmount(mro, currency);
  const locale = language === 'ar' ? 'ar-MR-u-nu-latn' : 'fr-MR';
  let formatted;
  try {
    formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(display);
  } catch {
    formatted = display.toFixed(2);
  }
  // Same as POS/thermal: amount then currency (Arabic after the number).
  if (language === 'ar') {
    return `\u2067${formatted}\u00A0${label}\u2069`;
  }
  return `${formatted} ${label}`;
}

function paymentLabel(method, language) {
  const m = String(method || '').toLowerCase();
  if (m === 'electronic') return getReceiptLabel('paymentElectronic', language);
  if (m === 'credit') {
    return language === 'ar' ? 'آجل' : 'Crédit';
  }
  return getReceiptLabel('paymentCash', language);
}

function pickProducts(source) {
  if (Array.isArray(source?.products)) return source.products;
  if (Array.isArray(source?.items)) return source.items;
  if (Array.isArray(source?.lineItems)) return source.lineItems;
  return [];
}

/** Same pack/unit money SSOT as thermal mapper + saleLineMath. */
function normalizeLineItem(item, language = 'fr') {
  const activeQty = getSaleLineActiveQuantity({
    ...item,
    quantity: item?.quantity ?? item?.qty ?? 0
  });
  const isPack =
    item?.sellMode === 'pack' &&
    Number(item?.sellingPackPrice) > 0 &&
    Number(item?.unitsPerPack) > 1;

  if (isPack) {
    const unitsPerPack = Math.round(Number(item.unitsPerPack));
    const packCount = activeQty / unitsPerPack;
    const lineTotal = getSaleLineActiveAmount({
      ...item,
      quantity: item?.quantity ?? item?.qty ?? 0
    });
    return {
      name: item?.name || item?.productName || getReceiptLabel('defaultArticle', language),
      quantity: Number.isFinite(packCount) && packCount > 0 ? Math.round(packCount * 1000) / 1000 : activeQty,
      unitPrice: Number(item.sellingPackPrice),
      lineTotal: Math.round(lineTotal * 100) / 100
    };
  }

  const unitPrice =
    getSaleLineUnitPrice(item) ||
    Number(
      item?.price ??
        item?.unitPrice ??
        item?.sellingPrice ??
        item?.catalogSellingPrice ??
        0
    ) ||
    0;
  const explicitLine = Number(item?.lineTotal ?? item?.total ?? item?.amount);
  const computed = getSaleLineActiveAmount({
    ...item,
    quantity: item?.quantity ?? item?.qty ?? 0
  });
  const lineTotal =
    Number.isFinite(explicitLine) && explicitLine > 0
      ? explicitLine
      : Math.round(computed * 100) / 100;

  return {
    name: item?.name || item?.productName || getReceiptLabel('defaultArticle', language),
    quantity: activeQty,
    unitPrice,
    lineTotal
  };
}

/**
 * @param {object} receiptData - normalized client receipt payload
 * @param {{ language?: string }} [options]
 * @returns {string}
 */
export function buildClientA5ReceiptHtml(receiptData, options = {}) {
  const language = normalizeReceiptLanguage(options.language || 'fr');
  const isRTL = language === 'ar';
  const locale = isRTL ? 'ar-MR' : 'fr-MR';
  const L = (key) => getReceiptLabel(key, language);

  const business = receiptData.businessInfo || {};
  const products = pickProducts(receiptData).map((item) => normalizeLineItem(item, language));
  const computedSubtotal = products.reduce((sum, item) => sum + (Number(item.lineTotal) || 0), 0);
  const subtotal =
    Number(receiptData.subtotal) > 0 ? Number(receiptData.subtotal) : computedSubtotal;
  const total =
    Number(receiptData.total ?? receiptData.totalAmount) > 0
      ? Number(receiptData.total ?? receiptData.totalAmount)
      : subtotal;
  const dateLabel = formatSafeDateTime(
    receiptData.date || new Date(),
    locale,
    { hour: '2-digit', minute: '2-digit' },
    '-'
  );

  const rows = products
    .map((item) => {
      const name = escapeHtml(item.name);
      const qty = item.quantity;
      const price = item.unitPrice;
      const lineTotal = item.lineTotal;
      return `<tr>
        <td>${name}</td>
        <td class="num">${escapeHtml(String(qty))}</td>
        <td class="num">${escapeHtml(formatMoney(price, language))}</td>
        <td class="num">${escapeHtml(formatMoney(lineTotal, language))}</td>
      </tr>`;
    })
    .join('');

  const businessName = escapeHtml(business.name || business.business_name || 'AMAN');
  const businessAddress = escapeHtml(business.address || '');
  const businessPhone = escapeHtml(business.phone || '');

  return `<!DOCTYPE html>
<html lang="${isRTL ? 'ar' : 'fr'}" dir="${isRTL ? 'rtl' : 'ltr'}">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(L('htmlTitleClient'))}</title>
  <style>
    ${buildPrintAdsCssVariables()}
    ${buildPrintAdsInkForceCss()}
    @page {
      size: A5 portrait;
      margin: 8mm;
    }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      height: auto;
      overflow: visible;
    }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      line-height: 1.35;
      padding: 4mm 2mm 10mm;
    }
    .sheet {
      width: 100%;
      max-width: 148mm;
      margin: 0 auto;
      padding-bottom: 6mm;
    }
    .header {
      text-align: center;
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 2px solid #111;
    }
    .header h1 {
      margin: 0 0 4px;
      font-size: 16px;
      letter-spacing: 0.02em;
    }
    .header .meta {
      font-size: 10px;
      color: #333;
    }
    .banner {
      text-align: center;
      font-weight: 700;
      font-size: 13px;
      margin: 8px 0 10px;
      text-transform: uppercase;
    }
    .info {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4px 12px;
      margin-bottom: 10px;
      font-size: 10px;
    }
    .info .label { color: #555; }
    .info .value { font-weight: 600; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0;
    }
    th, td {
      border-bottom: 1px solid #ddd;
      padding: 5px 4px;
      text-align: ${isRTL ? 'right' : 'left'};
      vertical-align: top;
    }
    th {
      border-bottom: 2px solid #111;
      font-size: 10px;
      text-transform: uppercase;
    }
    td.num, th.num { text-align: ${isRTL ? 'left' : 'right'}; white-space: nowrap; }
    .totals {
      margin-top: 10px;
      width: 100%;
      max-width: 240px;
      margin-${isRTL ? 'right' : 'left'}: auto;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .totals .row {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 3px 0;
      font-size: 11px;
    }
    .totals .row.total {
      font-weight: 700;
      font-size: 13px;
      border-top: 2px solid #111;
      margin-top: 4px;
      padding-top: 6px;
      padding-bottom: 4px;
    }
    .footer {
      margin-top: 14px;
      margin-bottom: 4mm;
      text-align: center;
      font-size: 10px;
      border-top: 1px dashed #999;
      padding-top: 8px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="header">
      <h1>${businessName}</h1>
      <div class="meta">
        ${businessAddress ? `${businessAddress}<br/>` : ''}
        ${businessPhone ? `${businessPhone}` : ''}
      </div>
    </div>
    <div class="banner">${escapeHtml(L('clientReceiptBanner'))}</div>
    <div class="info">
      <div><span class="label">${escapeHtml(L('orderNo'))}</span> <span class="value">${escapeHtml(receiptData.orderId || '—')}</span></div>
      <div><span class="label">${escapeHtml(L('dateTime'))}</span> <span class="value">${escapeHtml(dateLabel)}</span></div>
      <div><span class="label">${escapeHtml(L('cashier'))}</span> <span class="value">${escapeHtml(receiptData.workerName || L('defaultSeller'))}</span></div>
      <div><span class="label">${escapeHtml(L('payment'))}</span> <span class="value">${escapeHtml(paymentLabel(receiptData.paymentMethod, language))}</span></div>
      ${
        receiptData.customerName || receiptData.customerPhone
          ? `<div><span class="label">${escapeHtml(L('customer'))}</span> <span class="value">${escapeHtml(receiptData.customerName || '')}${receiptData.customerPhone ? ` · ${escapeHtml(receiptData.customerPhone)}` : ''}</span></div>`
          : ''
      }
    </div>
    <table>
      <thead>
        <tr>
          <th>${escapeHtml(L('product'))}</th>
          <th class="num">${escapeHtml(L('qty'))}</th>
          <th class="num">${escapeHtml(L('price'))}</th>
          <th class="num">${escapeHtml(L('total'))}</th>
        </tr>
      </thead>
      <tbody>
        ${rows || `<tr><td colspan="4">${escapeHtml(L('defaultArticle'))}</td></tr>`}
      </tbody>
    </table>
    <div class="totals">
      ${
        Number(receiptData.discount) > 0
          ? `<div class="row"><span>${escapeHtml(L('discount'))}</span><span>${escapeHtml(formatMoney(receiptData.discount, language))}</span></div>`
          : ''
      }
      ${
        Number(receiptData.tax) > 0
          ? `<div class="row"><span>${escapeHtml(L('tax'))}</span><span>${escapeHtml(formatMoney(receiptData.tax, language))}</span></div>`
          : ''
      }
      <div class="row total"><span>${escapeHtml(L('totalLabel'))}</span><span>${escapeHtml(formatMoney(total, language))}</span></div>
      ${
        String(receiptData.paymentMethod || '').toLowerCase() === 'cash'
          ? `<div class="row"><span>${escapeHtml(L('cashPaid'))}</span><span>${escapeHtml(formatMoney(receiptData.cashAmount, language))}</span></div>
             <div class="row"><span>${escapeHtml(L('change'))}</span><span>${escapeHtml(formatMoney(receiptData.change, language))}</span></div>`
          : ''
      }
    </div>
    <div class="footer">${escapeHtml(L('thankYou'))}</div>
  </div>
</body>
</html>`;
}
